<template>
  <div class="pct-list-container" v-if="plate_code">
    <div v-if="isLoading" class="text-center py-3">
      <b-spinner small variant="primary"></b-spinner>
    </div>
    <div v-else class="pct-intervals-list">
      <div v-for="intv in displayedIntervals" :key="intv" class="pct-interval-row">
        <!-- 左侧区间标签 -->
        <div class="pct-label-col">
          {{ formatIntervalLabel(intv) }}
        </div>
        
        <!-- 右侧个股列表 -->
        <div class="pct-stocks-col">
          <div v-if="!stocks[intv] || getFilteredStocks(stocks[intv]).length === 0" class="no-data">
            -
          </div>
          <div 
            v-else
            v-for="stock in getFilteredStocks(stocks[intv])" 
            :key="stock.stock_code"
            class="pct-stock-item"
            :class="getRowHighlightClass(stock.stock_name)"
            v-b-toggle.sidebar-right-stock
            @click="handleStockClick(stock)"
          >
            <span class="stock-name" :title="stock.stock_name">{{ stock.stock_name }}</span>
            <span class="stock-val">{{ formatPct(stock.cum_pct) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from "network/request.js"

export default {
  name: "PlateStockPctOnPlate",
  props: {
    plate_code: {
      type: String,
      default: ""
    },
    date1: {
      type: String,
      default: ""
    },
    daysBack: {
      type: Number,
      default: 10
    },
    stocksData: {
      type: Object,
      default: () => null
    },
    loadingProp: {
      type: Boolean,
      default: false
    },
    isBatch: {
      type: Boolean,
      default: false
    },
    highlightStockName: {
      type: String,
      default: null
    },
    filterStockCodes: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      stocks: {},
      intervals: ["20-40", "40-60", "60-80", "80-100", "100+"]
    };
  },
  computed: {
    displayedIntervals() {
      return ["100+", "80-100", "60-80", "40-60", "20-40"];
    },
    isLoading() {
      return this.isBatch ? this.loadingProp : this.loading;
    }
  },
  watch: {
    stocksData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.intervals = newVal.intervals || ["20-40", "40-60", "60-80", "80-100", "100+"];
          this.stocks = newVal.stocks || {};
        }
      }
    },
    plate_code: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchStocksPct();
      }
    },
    date1: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchStocksPct();
      }
    },
    daysBack: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.fetchStocksPct();
      }
    }
  },
  methods: {
    fetchStocksPct() {
      if (this.isBatch || this.stocksData) return;
      if (!this.plate_code || !this.date1) return;
      this.loading = true;
      request_zzquant({
        url: `/v3/market/plates/17/${this.plate_code}/stocks/pct`,
        params: {
          date1: this.date1,
          days: this.daysBack
        }
      })
      .then(res => {
        const resData = res.data || {};
        const innerData = resData.data || {};
        this.intervals = innerData.intervals || ["20-40", "40-60", "60-80", "80-100", "100+"];
        this.stocks = innerData.stocks || {};
      })
      .catch(err => {
        console.error("Fetch stocks pct error:", err);
        this.stocks = {};
      })
      .finally(() => {
        this.loading = false;
      });
    },
    getFilteredStocks(stocks) {
      if (!stocks) return [];
      if (!this.filterStockCodes) return stocks;
      const filterSet = new Set(this.filterStockCodes);
      return stocks.filter(stock => filterSet.has(stock.stock_code));
    },
    formatPct(val) {
      if (val === undefined || val === null) return "-";
      return (val > 0 ? "+" : "") + val.toFixed(1) + "%";
    },
    handleStockClick(item) {
      this.$emit("stock-clicked", item.stock_name);
      this.$emit("show_sidebar", {
        stock_code: item.stock_code,
        stock_name: item.stock_name,
        date1: this.date1
      });
    },
    getRowHighlightClass(stockName) {
      if (this.highlightStockName && this.highlightStockName === stockName) {
        return "highlight-clicked";
      }
      return "";
    },
    formatIntervalLabel(intv) {
      const map = {
        "100+": "100+",
        "80-100": "80+",
        "60-80": "60+",
        "40-60": "40+",
        "20-40": "20+"
      };
      return map[intv] || intv;
    }
  }
};
</script>

<style scoped>
.pct-list-container {
  display: flex;
  flex-direction: column;
  background-color: var(--bg-card, #ffffff);
  border: 1px solid var(--border-color, #e9ecef);
  border-radius: 4px;
  padding: 1px;
  min-height: 100px;
}

.pct-interval-row {
  display: flex;
  border-bottom: 1px solid var(--border-color, #f8f9fa);
}

.pct-interval-row:last-child {
  border-bottom: none;
}

.pct-label-col {
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  background-color: rgba(220, 53, 69, 0.08);
  color: #dc3545;
  border-right: 1px solid var(--border-color, #e9ecef);
  flex-shrink: 0;
  text-align: center;
  padding: 4px 2px;
}

body.theme-dark .pct-label-col {
  background-color: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
}

.pct-stocks-col {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 2px 0;
  overflow: hidden;
}

.no-data {
  font-size: 10px;
  color: var(--text-muted, #6c757d);
  text-align: center;
  padding: 4px 0;
}

.pct-stock-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 4px;
  margin: 1px 2px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 10px;
  transition: background-color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.03);
}

body.theme-dark .pct-stock-item {
  border-bottom-color: rgba(255, 255, 255, 0.03);
}

.pct-stock-item:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.stock-name {
  font-weight: 500;
  color: var(--text-main, #212529);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex-grow: 1;
  text-align: left;
}

body.theme-dark .stock-name {
  color: #ced4da;
}

.stock-val {
  font-weight: 600;
  color: #dc3545;
  margin-left: 4px;
  flex-shrink: 0;
}

body.theme-dark .stock-val {
  color: #ff6b6b;
}

.highlight-clicked {
  background-color: rgba(111, 66, 193, 0.15) !important;
  box-shadow: inset 0 0 0 1.5px rgba(111, 66, 193, 0.6);
  border-radius: 2px;
}

body.theme-dark .highlight-clicked {
  background-color: rgba(157, 107, 230, 0.2) !important;
  box-shadow: inset 0 0 0 1.5px rgba(157, 107, 230, 0.7);
}

@media (max-width: 768px) {
  .pct-label-col {
    width: 28px;
    font-size: 9px;
  }
  .pct-stock-item {
    font-size: 9px;
    padding: 1px 2px;
  }
}
</style>
